import React, { useState } from "react";
import cc from "classcat";
import { useLocalization } from "@fluent/react";
import {
  Alert,
  Button,
  Checkbox,
  Row,
  Table,
  TableInput,
  Tooltip,
  formatNumber,
} from "@narmi/design_system";
import { Dialog } from "cerulean";
import { formatAmountEntry } from "byzantine/src/dbbl/utils";
import styles from "./OrganizationRoles.module.scss";
import {
  RoleFormData,
  getAvailableLimitRails,
  getEnabledLimitFields,
  getLimitErrors,
  getOrganizationSecCodes,
  getSecCodeLimitErrors,
} from "./RoleFormUtils";

interface RoleDailyLimitsSectionProps {
  features?: Record<string, any>;
  formData: RoleFormData;
  /**
   * Not `onChange`: `ContextForm.Field` replaces that prop on its child with a handler that
   * stores the section's value under `limits`, which would overwrite the limits being edited.
   */
  onLimitChange: (update: Record<string, any>) => void;
  /** Set by `ContextForm.Field`, which also validates the limits when saving. */
  field?: string;
  error?: unknown;
}

/**
 * Daily limit per payment rail. A rail's limit is editable once the role can use that rail on at
 * least one account, and is saved as zero otherwise. Limits must be more than zero and within the
 * organization's limit; the ones that fail are listed above the table and marked on their rows.
 */
export default function RoleDailyLimitsSection({
  features = {},
  formData,
  onLimitChange,
  error,
}: RoleDailyLimitsSectionProps) {
  const { l10n } = useLocalization();
  const [isOrgLimitsDialogOpen, setIsOrgLimitsDialogOpen] = useState(false);
  const orgLimits = features.limits ?? {};
  const orgLimitKeys = Array.from(
    new Set(
      Object.keys(orgLimits).map((key) =>
        key === "unverified_ach_push_by_sec_code" ||
        key === "unverified_ach_pull_by_sec_code"
          ? key.replace("_by_sec_code", "")
          : key
      )
    )
  );

  const rails = getAvailableLimitRails(features);
  if (rails.length === 0) return null;
  const secCodes = getOrganizationSecCodes(features);

  // Fluent ids have to be literals, so the labels are looked up here by rail.
  const railLabels: Record<string, string> = {
    unverified_ach_push: l10n.getString("org-role-limits-row-ach-send"),
    unverified_ach_pull: l10n.getString("org-role-limits-row-ach-collect"),
    fednow_unverified: l10n.getString("org-role-limits-row-instant-delivery"),
    ach_passthrough_send: l10n.getString(
      "org-role-limits-row-ach-passthrough-send"
    ),
    ach_passthrough_collect: l10n.getString(
      "org-role-limits-row-ach-passthrough-collect"
    ),
    ach_push: l10n.getString("org-role-limits-row-external-transfers"),
    wire: l10n.getString("org-role-limits-row-domestic-wires"),
    international_wire: l10n.getString(
      "org-role-limits-row-international-wires"
    ),
  };
  const secCodeLabels: Record<string, string> = {
    PPD: l10n.getString("org-role-limit-type", { limitKey: "PPD" }),
    CCD: l10n.getString("org-role-limit-type", { limitKey: "CCD" }),
    WEB: l10n.getString("org-role-limit-type", { limitKey: "WEB" }),
  };
  const renderSecCodeTooltip = (limitKey: string) => {
    let text: string;
    if (
      limitKey === "unverified_ach_push" &&
      formData.ach_payment_limits &&
      secCodes.push.some((code) =>
        Object.prototype.hasOwnProperty.call(secCodeLabels, code)
      )
    ) {
      text = l10n.getString("org-role-limits-sec-codes-shared-send", {
        limit: `$${formatAmountEntry(formData.ach_payment_limits).value}`,
      });
    } else if (
      limitKey === "unverified_ach_pull" &&
      formData.collect_ach_payment_limits &&
      secCodes.pull.some((code) =>
        Object.prototype.hasOwnProperty.call(secCodeLabels, code)
      )
    ) {
      text = l10n.getString("org-role-limits-sec-codes-shared-collect", {
        limit: `$${formatAmountEntry(formData.collect_ach_payment_limits).value}`,
      });
    } else {
      return null;
    }
    return (
      <span className={styles.accessInfo}>
        <Tooltip text={text} side="top" maxWidth="400px">
          <span className="narmi-icon-info" />
        </Tooltip>
      </span>
    );
  };

  const enabledFields = getEnabledLimitFields(formData);
  const blockedSecCodeWarnings = (["push", "pull"] as const)
    .filter((direction) => {
      const field =
        direction === "push"
          ? "ach_payment_limits"
          : "collect_ach_payment_limits";
      const codes = secCodes[direction].filter((secCode) =>
        Object.prototype.hasOwnProperty.call(secCodeLabels, secCode)
      );
      return (
        enabledFields.has(field) &&
        codes.length > 0 &&
        codes.every(
          (secCode) => !formData.secCodeLimitEnabled?.[direction]?.[secCode]
        )
      );
    })
    .map((direction) => ({
      direction,
      message:
        direction === "push"
          ? l10n.getString("org-role-limits-all-sec-codes-blocked-send")
          : l10n.getString("org-role-limits-all-sec-codes-blocked-collect"),
    }));
  const limitErrors = error ? getLimitErrors(formData, features) : [];
  const secCodeLimitErrors = error
    ? getSecCodeLimitErrors(formData, features)
    : [];
  const errorFields = new Set(limitErrors.map(({ rail }) => rail.field));
  const secCodeErrorFields = new Set(
    secCodeLimitErrors.map(
      ({ direction, secCode }) => `${direction}:${secCode}`
    )
  );
  const errorMessages = [
    ...limitErrors.map(({ rail, error: limitError }) => ({
      rail: railLabels[rail.limitKey],
      error: limitError,
    })),
    ...secCodeLimitErrors.map(({ rail, secCode, error: limitError }) => ({
      rail: `${railLabels[rail.limitKey]} (${secCode})`,
      error: limitError,
    })),
  ].map(({ rail, error: limitError }) =>
    limitError.type === "zero"
      ? l10n.getString("org-role-limits-error-zero", {
          rail,
          zero: formatNumber(0, "currency"),
        })
      : l10n.getString("org-role-limits-error-max", {
          rail,
          max: formatNumber(limitError.max, "currency"),
        })
  );

  const onAmountChange =
    (field: string) => (event: React.ChangeEvent<HTMLInputElement>) =>
      onLimitChange({ [field]: formatAmountEntry(event.target.value).value });
  const onSecCodeAmountChange =
    (direction: "push" | "pull", secCode: string) =>
    (event: React.ChangeEvent<HTMLInputElement>) =>
      onLimitChange({
        secCodeLimits: {
          ...formData.secCodeLimits,
          [direction]: {
            ...formData.secCodeLimits?.[direction],
            [secCode]: formatAmountEntry(event.target.value).value,
          },
        },
      });
  const onSecCodeEnabledChange =
    (direction: "push" | "pull", secCode: string) => () =>
      onLimitChange({
        secCodeLimitEnabled: {
          ...formData.secCodeLimitEnabled,
          [direction]: {
            ...formData.secCodeLimitEnabled?.[direction],
            [secCode]: !formData.secCodeLimitEnabled?.[direction]?.[secCode],
          },
        },
      });

  const renderSecCodeRows = (direction: "push" | "pull") =>
    secCodes[direction].map((secCode) => {
      if (!Object.prototype.hasOwnProperty.call(secCodeLabels, secCode)) {
        return null;
      }
      const field = `${direction}:${secCode}`;
      const label = secCodeLabels[secCode];
      const value = formData.secCodeLimits?.[direction]?.[secCode] ?? "";
      const railEnabled = enabledFields.has(
        direction === "push"
          ? "ach_payment_limits"
          : "collect_ach_payment_limits"
      );
      const secCodeEnabled =
        formData.secCodeLimitEnabled?.[direction]?.[secCode] ?? false;
      let limitInput: React.ReactNode;
      if (railEnabled && secCodeEnabled) {
        limitInput = (
          <TableInput
            label={label}
            value={value ? `$${formatAmountEntry(value).value}` : ""}
            onChange={onSecCodeAmountChange(direction, secCode)}
            hasError={secCodeErrorFields.has(field)}
          />
        );
      } else {
        limitInput = (
          <span className={styles.limitNotEnabled}>
            {l10n.getString("org-role-limits-not-enabled")}
          </span>
        );
      }

      return (
        <Table.Row key={field}>
          <Table.Cell>
            <div className={styles.limitCell}>
              <Checkbox
                label={label}
                checked={secCodeEnabled}
                disabled={!railEnabled}
                onChange={onSecCodeEnabledChange(direction, secCode)}
              />
            </div>
          </Table.Cell>
          <Table.Cell>
            <div className={styles.limitCell}>{limitInput}</div>
          </Table.Cell>
        </Table.Row>
      );
    });

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <h4 className={styles.sectionTitle}>
          {l10n.getString("org-role-section-daily-limits")}
        </h4>
        <Button
          kind="tonal"
          size="xs"
          type="button"
          label={l10n.getString("org-role-view-organizational-limits")}
          onClick={() => setIsOrgLimitsDialogOpen(true)}
        />
      </div>
      <p className={styles.sectionDescription}>
        {l10n.getString("org-role-section-daily-limits-description")}
      </p>
      {blockedSecCodeWarnings.map(({ direction, message }) => (
        <Alert key={direction} kind="warn" isActive isDismissable={false}>
          {message}
        </Alert>
      ))}
      <div className={errorMessages.length ? styles.accountsError : undefined}>
        <Alert
          kind="error"
          isActive={errorMessages.length > 0}
          isDismissable={false}
        >
          {l10n.getString("org-role-limits-error-summary", {
            count: errorMessages.length,
          })}
          <ul className={styles.limitErrorList}>
            {errorMessages.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </Alert>
      </div>
      <div className={cc(["margin--top--l", styles.limitsTable])}>
        <Table
          kind="editable"
          colVisibility={["*", "*"]}
          colLayout={{
            s: ["1fr", "1fr"],
            m: ["1fr", "1fr"],
            l: ["1fr", "1fr"],
          }}
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>
                {l10n.getString("org-role-limits-column-payment-type")}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {l10n.getString("org-role-limits-column-daily-limit")}
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {rails.map((rail) => (
              <React.Fragment key={rail.field}>
                <Table.Row>
                  <Table.Cell>
                    <div className={styles.limitCell}>
                      {railLabels[rail.limitKey]}
                      {renderSecCodeTooltip(rail.limitKey)}
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <div className={styles.limitCell}>
                      {enabledFields.has(rail.field) ? (
                        <TableInput
                          label={railLabels[rail.limitKey]}
                          value={
                            formData[rail.field]
                              ? `$${
                                  formatAmountEntry(formData[rail.field]).value
                                }`
                              : ""
                          }
                          onChange={onAmountChange(rail.field)}
                          hasError={errorFields.has(rail.field)}
                        />
                      ) : (
                        <span className={styles.limitNotEnabled}>
                          {l10n.getString("org-role-limits-not-enabled")}
                        </span>
                      )}
                    </div>
                  </Table.Cell>
                </Table.Row>
                {rail.limitKey === "unverified_ach_push" &&
                  renderSecCodeRows("push")}
                {rail.limitKey === "unverified_ach_pull" &&
                  renderSecCodeRows("pull")}
              </React.Fragment>
            ))}
          </Table.Body>
        </Table>
      </div>
      <Dialog
        isOpen={isOrgLimitsDialogOpen}
        onUserDismiss={() => setIsOrgLimitsDialogOpen(false)}
        title={l10n.getString("org-role-organization-daily-limits-title")}
        headerStyle="plain"
        width="600px"
        footer={
          <Row alignItems="center" justifyContent="end">
            <Row.Item shrink>
              <Button
                label={l10n.getString("button-close")}
                onClick={() => setIsOrgLimitsDialogOpen(false)}
              />
            </Row.Item>
          </Row>
        }
      >
        <p className={styles.orgLimitsDescription}>
          {l10n.getString("org-role-organization-daily-limits-description")}
        </p>
        <div className={cc([styles.limitsTable, styles.orgLimitsTable])}>
          <Table
            colVisibility={["*", "*"]}
            colLayout={{
              s: ["1fr", "1fr"],
              m: ["1fr", "1fr"],
              l: ["1fr", "1fr"],
            }}
          >
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>
                  {l10n.getString("org-role-limits-column-payment-type")}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {l10n.getString("org-role-limits-column-organizational-limit")}
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {orgLimitKeys.map((limitKey) => {
                const limitValue = orgLimits[limitKey];
                const entries = Object.entries(
                  (limitValue as Record<string, any>) ?? {}
                );

                const numericPeriods = entries
                  .filter(
                    ([key, amount]) =>
                      !Number.isNaN(Number(key)) && Number(amount) >= 0
                  )
                  .sort(
                    ([periodA], [periodB]) => Number(periodA) - Number(periodB)
                  );

                const secCodeEntries =
                  limitKey === "unverified_ach_push" ||
                  limitKey === "unverified_ach_pull"
                    ? Object.entries(
                        orgLimits[`${limitKey}_by_sec_code`] ?? {}
                      )
                    : [];
                const secCodeSubLimits = secCodeEntries
                  .filter(
                    ([key, value]) =>
                      Object.prototype.hasOwnProperty.call(secCodeLabels, key) &&
                      Number.isNaN(Number(key)) &&
                      value &&
                      typeof value === "object"
                  )
                  .map(([secCode, secPeriods]) => {
                    const periods = Object.entries(
                      secPeriods as Record<string, any>
                    )
                      .filter(
                        ([period, amount]) =>
                          !Number.isNaN(Number(period)) && Number(amount) >= 0
                      )
                      .sort(
                        ([periodA], [periodB]) =>
                          Number(periodA) - Number(periodB)
                      );
                    return [secCode, periods] as const;
                  })
                  .filter(([, periods]) => periods.length > 0);

                if (
                  numericPeriods.length === 0 &&
                  secCodeSubLimits.length === 0
                ) {
                  return null;
                }

                return (
                  <React.Fragment key={limitKey}>
                    <Table.Row>
                      <Table.Cell>
                        <div
                          className={cc([
                            styles.limitCell,
                            styles.orgLimitLabel,
                          ])}
                        >
                          {l10n.getString("org-role-limit-type", { limitKey })}
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        <div
                          className={cc([
                            styles.limitCell,
                            styles.orgLimitValue,
                          ])}
                        >
                          {numericPeriods.map(([period, amount]) => (
                            <div key={period}>
                              {formatNumber(Number(amount), "currency")}{" "}
                              {l10n.getString("org-role-limit-period-per-days", {
                                days: Number(period),
                              })}
                            </div>
                          ))}
                        </div>
                      </Table.Cell>
                    </Table.Row>
                    {secCodeSubLimits.map(([secCode, periods]) => (
                      <Table.Row key={`${limitKey}-${secCode}`}>
                        <Table.Cell>
                          <div
                            className={cc([
                              styles.limitCell,
                              styles.orgLimitLabel,
                              styles.nestedLimitCell,
                            ])}
                          >
                            {secCodeLabels[secCode]}
                          </div>
                        </Table.Cell>
                        <Table.Cell>
                          <div
                            className={cc([
                              styles.limitCell,
                              styles.orgLimitValue,
                            ])}
                          >
                            {periods.map(([period, amount]) => (
                              <div key={period}>
                                {formatNumber(Number(amount), "currency")}{" "}
                                {l10n.getString(
                                  "org-role-limit-period-per-days",
                                  {
                                    days: Number(period),
                                  }
                                )}
                              </div>
                            ))}
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </React.Fragment>
                );
              })}
            </Table.Body>
          </Table>
        </div>
      </Dialog>
    </section>
  );
}
